import { usePlayerActions } from "@utils/player/core/usePlayer";
import Albums from "@components/Collection/artist/music/Albums";
import PopularSongs from "@components/Collection/artist/music/PopularSongs";
import LBox from "@components/LBox";
import type { Album, Song } from "@libs/wire";
import { useLocalSearchParams } from "expo-router";
import { useArtistProfileSuspense } from "@hooks/trpc/useArtists";
import LSuspense from "@components/LSuspense";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";

import { useCallback } from "react";
import { Platform } from "react-native";
import { useSharedSongLink } from "@hooks/useSharedSongLink";

interface MusicProps {
  artistId?: string;
  songId?: string;
}

const MusicComp = ({ artistId: propArtistId, songId }: MusicProps) => {
  const playerActions = usePlayerActions();

  const artistIdFromPath = useNavigationStore((state) => {
    const path = state.currentPath;
    return path.includes("/artist/")
      ? path.split("/artist/").at(1)?.split("/").at(0)
      : undefined;
  });

  const { artistId: paramArtistId } = useLocalSearchParams<{
    artistId: string;
  }>();

  const artistId = propArtistId ?? paramArtistId ?? artistIdFromPath;
  const {
    query: { data: artistProfile },
  } = useArtistProfileSuspense(artistId);

  const artist = artistProfile ?? { id: artistId, name: "" };
  const allAlbums: Album[] = artistProfile?.albums || [];
  const songs: Song[] = artistProfile?.popularSongs || [];

  const releases = allAlbums.filter((a) => a.artistIds.includes(artistId!));
  const appearsOn = allAlbums.filter((a) => !a.artistIds.includes(artistId!));

  const playFromCollection = playerActions.playFromCollection;

  // Resolve the song from a share link once its collection data is available.
  const playSongOnLink = useCallback(
    (sharedSongId: string) => {
      if (!artistId) return;

      playFromCollection({
        songId: sharedSongId,
        collectionType: "artist",
        collectionId: artistId,
        collectionName: artist.name,
        options: Platform.OS === "web" ? { autoPlay: false } : undefined,
      });
    },
    [artist.name, artistId, playFromCollection],
  );

  useSharedSongLink({ onPlay: playSongOnLink, songId, songs });

  return (
    <>
      <LBox paddingHorizontal="s4">
        <PopularSongs artist={artist} songs={songs} />
      </LBox>
      <Albums albums={releases} />
      <LBox marginTop="m1">
        <Albums albums={appearsOn} title="Appears On" />
      </LBox>
    </>
  );
};

const Music = ({ artistId, songId }: MusicProps) => {
  return (
    <LSuspense key={`${artistId ?? "unknown"}:${songId ?? "index"}`}>
      <MusicComp artistId={artistId} songId={songId} />
    </LSuspense>
  );
};

export default Music;
